import { supabase } from '../../shared/supabase';
import { calculateOrderTotals, createOrder } from './paymentService';
import { createStaffNotifications } from '../../shared/api/notificationService';

const LIVE_ORDER_STATUSES = ['pending', 'preparing', 'ready'];

class LiveOrderService {
  constructor() {
    this.LIVE_ORDER_STATUSES = LIVE_ORDER_STATUSES;
  }

  /**
   * Normalize food item properties for storage.
   * @private
   */
  _normalizeCartItem(item) {
    return {
      food_item_id: item.food_item_id || item.id,
      quantity: item.quantity,
      price: item.price,
      name: item.name,
      category: item.category,
      image: item.image,
      note: item.note || null,
    };
  }

  /**
   * Generate key for cart item comparison.
   * @private
   */
  _itemKey(item) {
    return `${item.food_item_id || item.id}-${item.category || ''}`;
  }

  /**
   * Get KOT number sequence.
   * @private
   */
  async _getNextKotNumber(orderId) {
    const { data, error } = await supabase
      .from('order_kots')
      .select('kot_number')
      .eq('order_id', orderId)
      .order('kot_number', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return (data?.kot_number || 0) + 1;
  }

  /**
   * Create staff notification alert on new KOT submission.
   * @private
   */
  async _notifyLiveOrderKot(order, kotNumber, isFirstKot) {
    try {
      await createStaffNotifications(
        {
          title: isFirstKot ? 'New Live Order' : `Live Order · KOT #${kotNumber}`,
          message: isFirstKot
            ? `Table ${order.table_number} started a live order (₹${Number(order.total).toFixed(2)})`
            : `Table ${order.table_number} added items — KOT #${kotNumber} (₹${Number(order.total).toFixed(2)} total)`,
          type: isFirstKot ? 'order_received' : 'kot_added',
          order_id: order.id,
          metadata: {
            order_id: order.id,
            order_number: order.order_number,
            table_number: order.table_number,
            kot_number: kotNumber,
            order_kind: 'live_order',
            total_amount: order.total,
          },
        },
        order.restaurant_id,
      );
    } catch (err) {
      console.warn('Live order notification failed:', err);
    }
  }

  /**
   * Get order item quantity in existing order
   */
  getOrderItemQty(cartItem, orderItems = []) {
    const key = this._itemKey(cartItem);
    const match = (orderItems || []).find((item) => this._itemKey(item) === key);
    return match?.quantity || 0;
  }

  /**
   * Items in cart that are not yet on the live order (or have a higher quantity).
   */
  getCartDeltaForKot(cartItems, orderItems = []) {
    const orderQtyByKey = new Map();
    (orderItems || []).forEach((item) => {
      orderQtyByKey.set(this._itemKey(item), item.quantity || 0);
    });

    const delta = [];
    for (const item of cartItems) {
      const orderQty = orderQtyByKey.get(this._itemKey(item)) || 0;
      const deltaQty = (item.quantity || 0) - orderQty;
      if (deltaQty > 0) {
        delta.push({ ...item, quantity: deltaQty });
      }
    }
    return delta;
  }

  /**
   * Merge cart items with existing items in order
   */
  mergeOrderItems(existingItems = [], newItems = []) {
    const map = new Map();

    const addToMap = (item) => {
      const key = `${item.food_item_id || item.id}-${item.category}`;
      const existing = map.get(key);
      if (existing) {
        map.set(key, {
          ...existing,
          quantity: existing.quantity + item.quantity,
        });
      } else {
        map.set(key, { ...item });
      }
    };

    existingItems.forEach(addToMap);
    newItems.map(this._normalizeCartItem).forEach(addToMap);

    return Array.from(map.values());
  }

  /**
   * Get current active live order for a table
   */
  async getActiveLiveOrder(restaurantId, tableNumber) {
    if (!restaurantId || !tableNumber) return null;

    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .eq('table_number', String(tableNumber))
      .eq('order_kind', 'live_order')
      .eq('payment_status', 'pending')
      .in('status', this.LIVE_ORDER_STATUSES)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data;
  }

  /**
   * Create a brand new live order or append to it if already active
   */
  async createLiveOrderWithKot({
    restaurantId,
    tableNumber,
    userId,
    customerName,
    customerEmail,
    customerPhone,
    items,
    specialInstructions,
  }) {
    const existing = await this.getActiveLiveOrder(restaurantId, tableNumber);
    if (existing) {
      return this.appendKotToLiveOrder(existing.id, restaurantId, items);
    }

    const normalizedItems = items.map(this._normalizeCartItem);
    const totals = calculateOrderTotals(normalizedItems);

    const order = await createOrder({
      restaurantId,
      userId,
      customerName,
      customerEmail,
      customerPhone,
      tableNumber,
      items: normalizedItems,
      paymentMethod: 'direct',
      paymentStatus: 'pending',
      status: 'preparing',
      specialInstructions,
      orderKind: 'live_order',
      kotCount: 1,
    });

    const { error: kotError } = await supabase.from('order_kots').insert([
      {
        order_id: order.id,
        restaurant_id: restaurantId,
        kot_number: 1,
        items: normalizedItems,
        subtotal: totals.subtotalAfterDiscount,
        status: 'sent',
      },
    ]);

    if (kotError) throw kotError;

    await this._notifyLiveOrderKot(order, 1, true);
    return order;
  }

  /**
   * Append a new KOT to an active live order
   */
  async appendKotToLiveOrder(orderId, restaurantId, items) {
    const { data: existingOrder, error: fetchError } = await supabase
      .from('orders')
      .select('*')
      .eq('id', orderId)
      .eq('order_kind', 'live_order')
      .single();

    if (fetchError) throw fetchError;
    if (!existingOrder) throw new Error('Live order not found');
    if (existingOrder.payment_status !== 'pending') {
      throw new Error('This live order is already paid and cannot be updated.');
    }

    const normalizedItems = items.map(this._normalizeCartItem);
    const kotTotals = calculateOrderTotals(normalizedItems);
    const mergedItems = this.mergeOrderItems(existingOrder.items || [], normalizedItems);
    const totals = calculateOrderTotals(mergedItems);
    const nextKotNumber = await this._getNextKotNumber(orderId);

    const { error: kotError } = await supabase.from('order_kots').insert([
      {
        order_id: orderId,
        restaurant_id: restaurantId,
        kot_number: nextKotNumber,
        items: normalizedItems,
        subtotal: kotTotals.subtotalAfterDiscount,
        status: 'sent',
      },
    ]);

    if (kotError) throw kotError;

    const orderPatch = {
      items: mergedItems,
      subtotal: totals.subtotalAfterDiscount,
      gst: totals.cgst + totals.sgst,
      discount: totals.discountAmount,
      total: totals.finalTotal,
      kot_count: nextKotNumber,
      updated_at: new Date().toISOString(),
      status: 'preparing',
    };

    const { data: updatedRows, error: updateError } = await supabase
      .from('orders')
      .update(orderPatch)
      .eq('id', orderId)
      .select();

    if (updateError) throw updateError;

    let updatedOrder = updatedRows?.[0];
    if (!updatedOrder) {
      const { data: refetched, error: refetchError } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .maybeSingle();

      if (refetchError) throw refetchError;
      updatedOrder = refetched || { ...existingOrder, ...orderPatch };
    }

    await this._notifyLiveOrderKot(updatedOrder, nextKotNumber, false);
    return updatedOrder;
  }
}

// Instantiate singleton
export const liveOrderService = new LiveOrderService();

// Backwards-compatible bindings
export const getOrderItemQty = liveOrderService.getOrderItemQty.bind(liveOrderService);
export const getCartDeltaForKot = liveOrderService.getCartDeltaForKot.bind(liveOrderService);
export const mergeOrderItems = liveOrderService.mergeOrderItems.bind(liveOrderService);
export const getActiveLiveOrder = liveOrderService.getActiveLiveOrder.bind(liveOrderService);
export const createLiveOrderWithKot =
  liveOrderService.createLiveOrderWithKot.bind(liveOrderService);
export const appendKotToLiveOrder = liveOrderService.appendKotToLiveOrder.bind(liveOrderService);

export default {
  getOrderItemQty,
  getCartDeltaForKot,
  mergeOrderItems,
  getActiveLiveOrder,
  createLiveOrderWithKot,
  appendKotToLiveOrder,
};
